import { OrgBaseInterface } from "../Shared/OrgBase.Interace";

/**
 * SMS campaigns on the Snowy bot (ENG-3114; spec `docs/specs/conversation-starters.md`,
 * "Caller 2"). An agent fires a Conversation Starter at a list of recipients; every recipient
 * gets its own row recording exactly what happened.
 *
 * Campaigns are VERBATIM-ONLY (Chuck, 2026-08-26): a fire never invokes the LLM. The model
 * runs only when a recipient replies. Nothing here schedules — a send fans out immediately.
 */

export type BotCampaignChannel = "sms";

/**
 * `simulated` (ENG-3373): the Outbound Gate withheld the text — the org's texts flag is off, or
 * outside production the number is not on the allowlist. The row keeps the resolved text and the
 * gate's reason in `skipReason`, so a dry run reads exactly like a real one minus the send.
 */
export type BotCampaignRecipientStatus = "queued" | "sent" | "simulated" | "skipped" | "failed";

/**
 * Machine-readable reasons recorded on a recipient row (`missing_variable:` carries the key).
 * The Outbound Gate's reasons (`flag_off`, `not_listed`, `allowlist_not_loaded`, `no_recipient`)
 * accompany `simulated`; `opted_out` is a `skipped` row the gate blocked on consent.
 */
export type BotCampaignSkipReason =
  | "flag_off"
  | "not_listed"
  | "allowlist_not_loaded"
  | "no_recipient"
  | "opted_out"
  | "no_phone"
  | "starter_not_found"
  | "starter_not_verbatim"
  | `missing_variable:${string}`;

export interface BotCampaignInterface extends OrgBaseInterface {
  /** The agent who sent it. */
  memberId: number;
  /** The starter this was composed from; `""` when the agent wrote the message from scratch. */
  starterSlug: string;
  /** Display label of the starter at send time, or "Custom message" for a written one. */
  starterLabel: string;
  /** True when the starter's own verbatim messages were sent; false when the agent customized. */
  usedDefault: boolean;
  /** The authored templates (1..3, `{{Type:Key}}` allowed) when customized; `[]` when the default was used. */
  messages: string[];
  /**
   * What the bot is told if a recipient replies. Snapshotted at compose time — from the starter
   * when one was picked, or written by the model from the agent's message when it wasn't. It never
   * writes the outbound text; the send is verbatim.
   */
  contextText: string;
  /**
   * The plain-language summary the agent was shown and approved ("you're saying there's a promo in
   * July?"). Kept for the receipt, so the record shows what a person actually agreed to, not just
   * what the model wrote underneath. Null on the starter path — nothing was drafted.
   */
  memberContext: string | null;
  recipientCount: number;
  channel: BotCampaignChannel;
}

export interface BotCampaignRecipientInterface extends OrgBaseInterface {
  campaignId: number;
  userId: number;
  locationId: number | null;
  phoneNumber: string | null;
  status: BotCampaignRecipientStatus;
  skipReason: string | null;
  /** The texts actually delivered, one per SMS (after variables + overflow split). Lossless. */
  resolvedMessages: string[] | null;
  conversationId: number | null;
  messageIds: string[] | null;
  externalSids: string[] | null;
  error: string | null;
  firedAt: Date | null;
}

/** Body of `POST /snowy-campaigns/org/{orgId}`. */
export interface BotCampaignCreateDTO {
  /** Omitted when the agent wrote the message from scratch. */
  starterSlug?: string;
  /** Send the starter's own verbatim messages. Mutually exclusive with `messages`. */
  useDefault: boolean;
  /** 1..3 authored messages, each ≤ `CAMPAIGN_MESSAGE_MAX_CHARS`, when customizing. */
  messages?: string[];
  /** Written path: the context approved in the compose step. Ignored when a starter is picked. */
  contextText?: string;
  /** Written path: the plain-language summary the agent approved. */
  memberContext?: string;
  recipients: Array<{ userId: number; locationId?: number | null }>;
}

/** Ask the model to turn an agent's message into a priming context plus a summary to confirm. */
export interface BotCampaignDraftContextRequest {
  /** The message the agent wrote, as it will be sent. */
  message: string;
  /**
   * The agent's own description of intent, when they rejected the summary and wrote their own.
   * Present on a re-draft; the model works from this instead of inferring from the message alone.
   */
  intent?: string;
}

export interface BotCampaignDraftContext {
  /** What the bot is told if a recipient replies. Never shown to the agent. */
  contextText: string;
  /** Plain-language read-back for the agent to approve or correct. */
  memberContext: string;
}

/** A starter an agent may pick — only verbatim SMS starters qualify. */
export interface BotCampaignStarterOption {
  slug: string;
  label: string;
  verbatimMessages: string[];
  /** Snapshotted onto the campaign at send time — what the bot is told if a recipient replies. */
  contextText: string;
  source: "org" | "parent" | "default";
}

export interface BotCampaignSummary extends BotCampaignInterface {
  sentCount: number;
  /** Recipients the Outbound Gate withheld (ENG-3373): built and recorded, never sent. */
  simulatedCount: number;
  skippedCount: number;
  failedCount: number;
  queuedCount: number;
}

/** Authoring caps (spec): up to three messages, 240 characters each, leaving headroom for variables. */
export const CAMPAIGN_MAX_MESSAGES = 3;
export const CAMPAIGN_MESSAGE_MAX_CHARS = 240;

/**
 * After variable injection a message longer than this is split at sentence/word boundaries
 * into several SMS — even past three delivered messages (decided 2026-08-23). Two GSM-7
 * segments; long enough that authored 240-char texts with short variables never split.
 */
/**
 * Longest a single outbound campaign text may run before it is split (Chuck, 2026-08-28).
 * One number, one place — raise or lower it here and every send follows.
 */
export const SMS_SPLIT_THRESHOLD = 240;

/** Payload of the Bull fan-out job → the bot's `POST /starters/fire`. */
export interface BotCampaignFireRequest {
  orgId: number;
  campaignId: number;
  recipientId: number;
}

export type BotCampaignFireResult =
  | { status: "sent"; conversationId: number; messageCount: number }
  | { status: "simulated"; conversationId: number; messageCount: number; reason: string }
  | { status: "skipped"; reason: string }
  | { status: "failed"; error: string }
  | { status: "already_processed"; previous: BotCampaignRecipientStatus };
